import { useMutation } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { api, type WorkerRow } from '../../api';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { EmptyState, Panel, StatusBadge, since } from '../../components/kit';
import { LATE_AFTER_MS } from './shared';

export function IdleWarning({ workers, queued }: { workers: WorkerRow[]; queued: number }) {
  const live = workers.filter((worker) => Date.now() - Date.parse(worker.seenAt) < LATE_AFTER_MS);
  if (queued === 0 || live.length > 0) return null;
  return (
    <div className="mb-4 rounded-[7px] border border-warn/30 bg-warn-soft px-4 py-3">
      <p className="text-[13px] font-medium text-warn">Nothing is listening to this queue</p>
      <p className="mt-1 text-[12px] text-ink-2">
        {workers.length === 0
          ? 'No worker has ever reported in. Start one where the repositories are, or check that the worker container is running.'
          : `The last worker checked in ${since(workers[0]!.seenAt)} ago. Tasks will sit here until one comes back.`}
      </p>
    </div>
  );
}

export function Workers({ workers, isAdmin, onChanged }: { workers?: WorkerRow[]; isAdmin: boolean; onChanged: () => void }) {
  const forget = useMutation({
    mutationFn: (id: string) => api.del(`/api/workers/${encodeURIComponent(id)}`),
    onSuccess: onChanged,
  });
  if (!workers) return <Skeleton className="h-20 w-full" />;
  return (
    <Panel description="Processes that claim tasks and run the agent. Each reports in every 30 seconds; one that stops is how a stuck queue explains itself. A silent row is a worker that is gone — forget it, or leave it and it clears itself within six hours.">
      {workers.length === 0 ? (
        <EmptyState
          title="No worker has reported in"
          hint="A worker runs where the repositories are: `taskpilot worker`, or the worker service in the compose file. Without one, queued tasks are never claimed."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[7.5rem]">State</TableHead>
              <TableHead>Worker</TableHead>
              <TableHead className="hidden md:table-cell">Serves</TableHead>
              <TableHead className="hidden text-right lg:table-cell">Build</TableHead>
              <TableHead className="text-right">Running</TableHead>
              <TableHead className="text-right">Last seen</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {workers.map((worker) => {
              const late = Date.now() - Date.parse(worker.seenAt) >= LATE_AFTER_MS;
              return (
                <TableRow key={worker.id}>
                  <TableCell>
                    <StatusBadge state={late ? 'failed' : worker.running > 0 ? 'running' : 'done'}>
                      {late ? 'Silent' : worker.running > 0 ? 'Working' : 'Idle'}
                    </StatusBadge>
                  </TableCell>
                  <TableCell className="data">{worker.id}</TableCell>
                  <TableCell className="hidden text-[12.5px] md:table-cell">
                    {worker.tenants.length === 0 ? 'every tenant' : worker.tenants.join(', ')}
                  </TableCell>
                  <TableCell className="data hidden text-right text-ink-3 lg:table-cell">
                    {/* The commit when the builder passed one; otherwise the build
                        time, which is what actually catches a worker left behind
                        on an old image. */}
                    {worker.version && worker.version !== 'unknown' && worker.version !== 'dev'
                      ? worker.version.slice(0, 7)
                      : worker.builtAt
                        ? new Date(worker.builtAt).toLocaleString([], {
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : 'unknown'}
                  </TableCell>
                  <TableCell className="data text-right">
                    {worker.running} / {worker.maxParallel}
                  </TableCell>
                  <TableCell className="data text-right text-ink-2">{since(worker.seenAt)} ago</TableCell>
                  <TableCell className="text-right">
                    {/* Only a silent row is worth dismissing, and dismissing one
                        is safe: a worker still alive reports back in 30 seconds. */}
                    {late && isAdmin && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        title={`Forget ${worker.id}`}
                        disabled={forget.isPending}
                        onClick={() => forget.mutate(worker.id)}
                      >
                        <Trash2 className="size-3.5" aria-hidden />
                        <span className="sr-only">Forget {worker.id}</span>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Panel>
  );
}

/**
 * The ledger is the surface that outlives the others: it keeps every finished run,
 * so it filters and pages rather than stopping at whatever the dashboard felt like
 * sending. Paging is by the last row's start time — rows arrive while you read.
 */
